import React, { useEffect, useState, memo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Card, CardBody } from '../components/ui/Card';
import { CtaSection } from '../components/sections/CtaSection';
import { ProgramDetailModal } from '../components/ui/ProgramDetailModal';
import { SearchIcon, FilterIcon, CalendarIcon, DollarSignIcon, UsersIcon, XIcon, CheckIcon } from 'lucide-react';
export function ComebackPrograms() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedDurations, setSelectedDurations] = useState<string[]>([]);
  const [priceRange, setPriceRange] = useState([0, 5000]);
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Program data
  const programs = [{
    id: 'boxing-bootcamp',
    title: 'Boxing & Fitness Bootcamp with Chris Clements',
    category: 'Fitness',
    description: 'Train like a champion with professional boxer Chris Clements in an intensive boxing and fitness bootcamp designed to push your limits and transform your mindset.',
    duration: '3 days, 2 nights',
    price: '$1,599 per person',
    priceValue: 1599,
    groupSize: '15-25 participants',
    dates: ['Jan 26-28, 2025'],
    location: 'Hard Rock Hotel Cancun, Mexico',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/9avHThGYydRCkXZU4SoNUQ/Boxing.webp',
    hostImage: 'https://uploadthingy.s3.us-west-1.amazonaws.com/9jNa6v9iwd3jG5mHFF8SXo/Chris2.jpg',
    hostName: 'Chris Clements',
    hostBio: 'Professional boxer and fitness coach with over 15 years of experience training athletes at all levels. Chris combines technical boxing expertise with high-intensity conditioning to help participants push past their limits and develop mental toughness. His unique approach focuses on building confidence, discipline, and resilience both in and out of the ring.',
    highlights: ['Daily boxing training sessions with Chris Clements', 'High-intensity interval training (HIIT) workouts', 'Nutrition and recovery workshops', 'Mental toughness and mindset coaching', 'All-inclusive Hard Rock Hotel accommodations', 'Group sparring and technique refinement']
  }, {
    id: 'singles-social',
    title: 'Singles Social',
    category: 'Social',
    description: 'Connect with like-minded singles in a relaxed beach setting with organized social activities and adventures.',
    duration: '3 days, 2 nights',
    price: '$1,299 per person',
    priceValue: 1299,
    groupSize: '20-30 participants',
    dates: ['May 15-17, 2024', 'July 10-12, 2024', 'Sept 5-7, 2024'],
    location: 'Riviera Maya, Mexico',
    image: 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
    highlights: ['Beach volleyball and group activities', 'Sunset cocktail mixers', 'Optional excursions to local attractions', 'Professional matchmaking opportunities', 'Beachfront accommodations']
  }, {
    id: 'couples-reset',
    title: 'Couples Reset & Vow-Renewal',
    category: 'Relationship',
    description: 'Reconnect and strengthen your relationship with a meaningful retreat designed for couples seeking renewal.',
    duration: '4 days, 3 nights',
    price: '$2,499 per couple',
    priceValue: 1249.5,
    groupSize: '10-15 couples',
    dates: ['Feb 14-17, 2024', 'June 20-23, 2024', 'Oct 12-15, 2024'],
    location: 'Tulum, Mexico',
    image: 'https://images.unsplash.com/photo-1546032996-6dfacbacbf3f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2067&q=80',
    highlights: ['Private beach vow renewal ceremony (non-ceremonial)', 'Couples massage sessions', 'Relationship workshops with experts', 'Romantic sunset dinner', 'Luxury suite accommodations']
  }, {
    id: 'friends-reunion',
    title: 'Friends Reunion Weekend',
    category: 'Social',
    description: 'Reconnect with old friends in a stunning beach setting with activities designed to strengthen bonds and create new memories.',
    duration: '3 days, 2 nights',
    price: '$1,199 per person',
    priceValue: 1199,
    groupSize: 'Custom groups of 6-20 friends',
    dates: ['March 22-24, 2024', 'Aug 16-18, 2024', 'Nov 8-10, 2024'],
    location: 'Playa del Carmen, Mexico',
    image: 'https://images.unsplash.com/photo-1559494007-9f5847c49d94?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1974&q=80',
    highlights: ['Private group activities customized to your interests', 'Group photo sessions', 'Beach bonfire night', 'Optional group excursions', 'Shared villa accommodations']
  }, {
    id: 'family-beach',
    title: 'Family Beach & Kids Leadership Camp',
    category: 'Family',
    description: 'The perfect family getaway combining relaxation for parents and enriching activities for children.',
    duration: '5 days, 4 nights',
    price: '$3,999 for family of four',
    priceValue: 999.75,
    groupSize: '10-15 families',
    dates: ['June 10-14, 2024', 'July 22-26, 2024', 'Aug 12-16, 2024'],
    location: 'Cancun, Mexico',
    image: 'https://images.unsplash.com/photo-1535131749006-b7f58c99034b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
    highlights: ['Kids leadership program with certified coaches', 'Family beach Olympics', 'Parent relaxation time', 'Marine biology excursions for kids', 'Family-friendly resort accommodations']
  }, {
    id: 'wellness-reset',
    title: 'Wellness Reset: Yoga & Breathwork',
    category: 'Wellness',
    description: 'Rejuvenate your mind, body, and spirit with a transformative wellness retreat focused on yoga and breathwork practices.',
    duration: '6 days, 5 nights',
    price: '$2,299 per person',
    priceValue: 2299,
    groupSize: '15-20 participants',
    dates: ['Jan 15-20, 2024', 'April 10-15, 2024', 'Oct 5-10, 2024'],
    location: 'Tulum, Mexico',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/o1fjDw612HCk6Xa8RMhNyh/Yoga.webp',
    highlights: ['Daily yoga sessions with expert instructors', 'Breathwork and meditation workshops', 'Nutritional counseling', 'Beachfront sunrise yoga', 'Eco-friendly resort accommodations']
  }, {
    id: 'golf-beach',
    title: 'Golf & Beach Games Weekend',
    category: 'Sports',
    description: 'The perfect blend of championship golf and beach relaxation for sports enthusiasts.',
    duration: '4 days, 3 nights',
    price: '$1,899 per person',
    priceValue: 1899,
    groupSize: '12-24 participants',
    dates: ['Feb 22-25, 2024', 'May 17-20, 2024', 'Nov 14-17, 2024'],
    location: 'Riviera Maya, Mexico',
    image: 'https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
    highlights: ['Rounds at championship golf courses', 'Beach volleyball tournaments', 'Golf clinics with pros', 'Evening social events', 'Luxury resort accommodations']
  }, {
    id: 'family-adventure',
    title: 'Family Adventure: Cenotes & Reefs',
    category: 'Family',
    description: 'An action-packed family adventure exploring the natural wonders of the Yucatan Peninsula.',
    duration: '5 days, 4 nights',
    price: '$4,499 for family of four',
    priceValue: 1124.75,
    groupSize: '8-10 families',
    dates: ['March 15-19, 2024', 'July 8-12, 2024', 'Dec 26-30, 2024'],
    location: 'Riviera Maya, Mexico',
    image: 'https://images.unsplash.com/photo-1544551763-92ab472cad5d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
    highlights: ['Guided cenote explorations', 'Snorkeling at coral reefs', 'Mayan ruins visits', 'Jungle zip-lining adventure', 'Family-friendly resort accommodations']
  }, {
    id: 'women-leadership',
    title: 'Women in Leadership & Wellness',
    category: 'Professional',
    description: 'Empower yourself with a retreat designed specifically for women leaders combining professional development and wellness.',
    duration: '5 days, 4 nights',
    price: '$2,499 per person',
    priceValue: 2499,
    groupSize: '15-20 participants',
    dates: ['March 8-12, 2024', 'Sept 20-24, 2024', 'Nov 15-19, 2024'],
    location: 'Tulum, Mexico',
    image: 'https://images.unsplash.com/photo-1590523277543-a94d2e4eb00b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1932&q=80',
    highlights: ['Leadership workshops with executive coaches', 'Networking opportunities', 'Wellness and self-care sessions', 'Mastermind groups', 'Luxury boutique hotel accommodations']
  }, {
    id: 'community-offsite',
    title: 'Desta Community Offsite Weekend',
    category: 'Community',
    description: 'Join the Desta community for a weekend of connection, learning, and relaxation with like-minded individuals.',
    duration: '3 days, 2 nights',
    price: '$999 per person',
    priceValue: 999,
    groupSize: '30-50 participants',
    dates: ['April 26-28, 2024', 'Aug 23-25, 2024', 'Oct 18-20, 2024'],
    location: 'Playa del Carmen, Mexico',
    image: 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2069&q=80',
    highlights: ['Community building activities', 'Knowledge sharing sessions', 'Beach social events', 'Optional group excursions', 'All-inclusive resort accommodations']
  }, {
    id: 'yacht-social',
    title: 'Yacht Social & Sunset Jams',
    category: 'Luxury',
    description: 'Experience the ultimate luxury weekend with yacht parties, live music, and exclusive beach events.',
    duration: '3 days, 2 nights',
    price: '$2,999 per person',
    priceValue: 2999,
    groupSize: '20-30 participants',
    dates: ['May 24-26, 2024', 'July 19-21, 2024', 'Sept 13-15, 2024'],
    location: 'Cancun, Mexico',
    image: 'https://images.unsplash.com/photo-1605281317010-fe5ffe798166?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2072&q=80',
    highlights: ['Private yacht cruise with DJ', 'Sunset beach party', 'VIP nightlife experiences', 'Gourmet dining experiences', 'Luxury beachfront accommodations']
  }, {
    id: 'kids-sports',
    title: 'Kids Sports & Leadership Camp',
    category: 'Youth',
    description: 'A specialized program for young athletes combining sports training with leadership development.',
    duration: '7 days, 6 nights',
    price: '$1,899 per child',
    priceValue: 1899,
    groupSize: '20-30 participants (ages 10-16)',
    dates: ['June 16-22, 2024', 'July 14-20, 2024', 'Aug 4-10, 2024'],
    location: 'Cancun, Mexico',
    image: 'https://images.unsplash.com/photo-1526307616774-60d0098f7642?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2071&q=80',
    highlights: ['Professional sports coaching', 'Leadership and teamwork workshops', 'Beach sports competitions', 'Water sports training', 'Supervised resort accommodations']
  }, {
    id: 'startup-bootcamp',
    title: 'Build on the Beach - The Entrepreneurs Bootcamp',
    category: 'Professional',
    description: 'An intensive program for entrepreneurs combining business development workshops with networking in a relaxed setting.',
    duration: '5 days, 4 nights',
    price: '$2,299 per person',
    priceValue: 2299,
    groupSize: '15-25 participants',
    dates: ['Feb 5-9, 2024', 'May 6-10, 2024', 'Oct 7-11, 2024'],
    location: 'Playa del Carmen, Mexico',
    image: 'https://images.unsplash.com/photo-1517048676732-d65bc937f952?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
    highlights: ['Mentorship from successful entrepreneurs', 'Pitch practice sessions', 'Networking events', 'Business strategy workshops', 'Boutique hotel accommodations']
  }, {
    id: 'founders-investors',
    title: 'Founders & Investors Circle (invite-only)',
    category: 'Exclusive',
    description: 'An exclusive gathering for successful founders and investors to connect, share insights, and explore opportunities.',
    duration: '4 days, 3 nights',
    price: '$4,999 per person',
    priceValue: 4999,
    groupSize: '15-20 participants (invitation only)',
    dates: ['March 1-4, 2024', 'Sept 26-29, 2024', 'Dec 5-8, 2024'],
    location: 'Private Villa, Riviera Maya, Mexico',
    image: 'https://images.unsplash.com/photo-1540541338287-41700207dee6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
    highlights: ['Curated networking events', 'Deal flow sessions', 'Thought leadership roundtables', 'Luxury experiences', 'Private villa accommodations']
  }, {
    id: 'nutrition-wellness',
    title: 'Nutrition & Wellness Transformation',
    category: 'Wellness',
    description: 'Transform your relationship with food and discover sustainable nutrition strategies with expert guidance in a stunning beach setting.',
    duration: '5 days, 4 nights',
    price: '$2,499 per person',
    priceValue: 2499,
    groupSize: '15-20 participants',
    dates: ['March 8-12, 2024', 'Sept 20-24, 2024', 'Nov 15-19, 2024'],
    location: 'Tulum, Mexico',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/aEgUSDVN3SaRCTgZHE6TX2/Nutrition.webp',
    highlights: ['Personalized nutrition assessments', 'Cooking workshops with healthy recipes', 'Mindful eating practices', 'Farm-to-table dining experiences', 'Luxury boutique hotel accommodations', 'Meal planning and prep strategies']
  }];
  // Get all unique categories
  const categories = [...new Set(programs.map(program => program.category))];
  // Get all unique durations
  const durations = [...new Set(programs.map(program => program.duration))];
  // Get all months from program dates
  const getMonthsFromDates = () => {
    const months = new Set();
    programs.forEach(program => {
      program.dates.forEach(date => {
        const monthMatch = date.match(/([A-Za-z]+)\s\d+/);
        if (monthMatch && monthMatch[1]) {
          months.add(monthMatch[1]);
        }
      });
    });
    return Array.from(months);
  };
  const months = getMonthsFromDates();
  // Filter programs based on search and filters
  const filteredPrograms = programs.filter(program => {
    // Search term filter
    const matchesSearch = program.title.toLowerCase().includes(searchTerm.toLowerCase()) || program.description.toLowerCase().includes(searchTerm.toLowerCase());
    // Category filter
    const matchesCategory = selectedCategories.length === 0 || selectedCategories.includes(program.category);
    // Duration filter
    const matchesDuration = selectedDurations.length === 0 || selectedDurations.includes(program.duration);
    // Price range filter
    const matchesPrice = program.priceValue >= priceRange[0] && program.priceValue <= priceRange[1];
    // Month filter
    const matchesMonth = !selectedMonth || program.dates.some(date => date.includes(selectedMonth));
    return matchesSearch && matchesCategory && matchesDuration && matchesPrice && matchesMonth;
  });
  // Check URL for program parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const programId = params.get('program');
    if (programId) {
      const program = programs.find(p => p.id === programId);
      if (program) {
        setSelectedProgram(program);
        setIsModalOpen(true);
      }
    }
  }, [location]);
  const handleProgramClick = program => {
    setSelectedProgram(program);
    setIsModalOpen(true);
  };
  const handleBookNow = program => {
    navigate('/booking', {
      state: {
        program
      }
    });
  };
  const toggleCategory = category => {
    setSelectedCategories(prev => prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]);
  };
  const toggleDuration = duration => {
    setSelectedDurations(prev => prev.includes(duration) ? prev.filter(d => d !== duration) : [...prev, duration]);
  };
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategories([]);
    setSelectedDurations([]);
    setPriceRange([0, 5000]);
    setSelectedMonth('');
  };
  return <div className="pt-20 bg-[#F4E8D0]/30" data-id="element-1057">
      {/* Hero */}
      <section className="bg-[#0099A1] py-20" data-id="element-1058">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1059">
          <div className="max-w-3xl" data-id="element-1060">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-1061">
              Comeback Programs
            </h1>
            <p className="text-xl text-white/90" data-id="element-1062">
              Experience the magic of Riviera Maya with our curated programs for
              individuals, couples, families, and groups.
            </p>
            <div className="mt-6" data-id="element-1063">
              <Button variant="primary" size="lg" to="/booking" className="bg-[#FF6B65] hover:bg-[#FF6B65]/90" data-id="element-1064">
                Book Any Program
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Search and Filters */}
      <section className="py-8 bg-white sticky top-16 z-20 border-b shadow-sm" data-id="element-1065">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1066">
          <div className="flex flex-col md:flex-row gap-4" data-id="element-1067">
            <div className="relative flex-grow" data-id="element-1068">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" data-id="element-1069" />
              <input type="text" placeholder="Search programs..." className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-[#0099A1] focus:border-[#0099A1]" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-1070" />
            </div>
            <Button variant="outline" className="flex items-center" onClick={() => setShowFilters(!showFilters)} data-id="element-1071">
              <FilterIcon className="h-5 w-5 mr-2" data-id="element-1072" />
              Filters{' '}
              {selectedCategories.length > 0 || selectedDurations.length > 0 || selectedMonth || priceRange[0] > 0 || priceRange[1] < 5000 ? `(${selectedCategories.length + selectedDurations.length + (selectedMonth ? 1 : 0) + (priceRange[0] > 0 || priceRange[1] < 5000 ? 1 : 0)})` : ''}
            </Button>
          </div>
          {showFilters && <div className="mt-4 p-4 bg-gray-50 rounded-md" data-id="element-1073">
              <div className="flex justify-between items-center mb-4" data-id="element-1074">
                <h3 className="text-lg font-semibold text-[#374047]" data-id="element-1075">
                  Filters
                </h3>
                <button className="text-sm text-[#0099A1] hover:underline" onClick={clearFilters} data-id="element-1076">
                  Clear all filters
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-1077">
                {/* Categories */}
                <div data-id="element-1078">
                  <h4 className="font-medium text-[#374047] mb-2" data-id="element-1079">
                    Categories
                  </h4>
                  <div className="space-y-2" data-id="element-1080">
                    {categories.map((category, index) => <label key={index} className="flex items-center cursor-pointer" data-id="element-1081">
                        <input type="checkbox" className="rounded text-[#0099A1] focus:ring-[#0099A1] h-4 w-4" checked={selectedCategories.includes(category)} onChange={() => toggleCategory(category)} data-id="element-1082" />
                        <span className="ml-2 text-sm text-[#374047]" data-id="element-1083">
                          {category}
                        </span>
                      </label>)}
                  </div>
                </div>
                {/* Duration */}
                <div data-id="element-1084">
                  <h4 className="font-medium text-[#374047] mb-2" data-id="element-1085">Duration</h4>
                  <div className="space-y-2" data-id="element-1086">
                    {durations.map((duration, index) => <label key={index} className="flex items-center cursor-pointer" data-id="element-1087">
                        <input type="checkbox" className="rounded text-[#0099A1] focus:ring-[#0099A1] h-4 w-4" checked={selectedDurations.includes(duration)} onChange={() => toggleDuration(duration)} data-id="element-1088" />
                        <span className="ml-2 text-sm text-[#374047]" data-id="element-1089">
                          {duration}
                        </span>
                      </label>)}
                  </div>
                </div>
                {/* Price Range */}
                <div data-id="element-1090">
                  <h4 className="font-medium text-[#374047] mb-2" data-id="element-1091">
                    Price Range (per person)
                  </h4>
                  <div className="px-2" data-id="element-1092">
                    <input type="range" min="0" max="5000" step="100" value={priceRange[1]} onChange={e => setPriceRange([priceRange[0], parseInt(e.target.value)])} className="w-full" data-id="element-1093" />
                    <div className="flex justify-between text-sm text-[#374047]/70" data-id="element-1094">
                      <span data-id="element-1095">${priceRange[0]}</span>
                      <span data-id="element-1096">Up to ${priceRange[1]}</span>
                    </div>
                  </div>
                </div>
                {/* Month */}
                <div data-id="element-1097">
                  <h4 className="font-medium text-[#374047] mb-2" data-id="element-1098">Month</h4>
                  <select value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#0099A1] focus:border-[#0099A1]" data-id="element-1099">
                    <option value="" data-id="element-1100">Any Month</option>
                    {months.map((month, index) => <option key={index} value={month} data-id="element-1101">
                        {month}
                      </option>)}
                  </select>
                </div>
              </div>
              {/* Active filters */}
              {(selectedCategories.length > 0 || selectedDurations.length > 0 || selectedMonth || priceRange[0] > 0 || priceRange[1] < 5000) && <div className="mt-4 flex flex-wrap gap-2" data-id="element-1102">
                  {selectedCategories.map((category, index) => <div key={index} className="bg-[#0099A1]/10 text-[#0099A1] px-3 py-1 rounded-full text-sm flex items-center" data-id="element-1103">
                      {category}
                      <button onClick={() => toggleCategory(category)} className="ml-2" data-id="element-1104">
                        <XIcon className="h-3 w-3" data-id="element-1105" />
                      </button>
                    </div>)}
                  {selectedDurations.map((duration, index) => <div key={index} className="bg-[#0099A1]/10 text-[#0099A1] px-3 py-1 rounded-full text-sm flex items-center" data-id="element-1106">
                      {duration}
                      <button onClick={() => toggleDuration(duration)} className="ml-2" data-id="element-1107">
                        <XIcon className="h-3 w-3" data-id="element-1108" />
                      </button>
                    </div>)}
                  {selectedMonth && <div className="bg-[#0099A1]/10 text-[#0099A1] px-3 py-1 rounded-full text-sm flex items-center" data-id="element-1109">
                      {selectedMonth}
                      <button onClick={() => setSelectedMonth('')} className="ml-2" data-id="element-1110">
                        <XIcon className="h-3 w-3" data-id="element-1111" />
                      </button>
                    </div>}
                  {(priceRange[0] > 0 || priceRange[1] < 5000) && <div className="bg-[#0099A1]/10 text-[#0099A1] px-3 py-1 rounded-full text-sm flex items-center" data-id="element-1112">
                      ${priceRange[0]} - ${priceRange[1]}
                      <button onClick={() => setPriceRange([0, 5000])} className="ml-2" data-id="element-1113">
                        <XIcon className="h-3 w-3" data-id="element-1114" />
                      </button>
                    </div>}
                </div>}
            </div>}
        </div>
      </section>

      {/* Programs List */}
      <section className="py-12 bg-[#F4E8D0]/30" data-id="element-1115">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1116">
          {filteredPrograms.length > 0 ? <>
              <div className="mb-6" data-id="element-1117">
                <h2 className="text-2xl font-bold text-[#374047]" data-id="element-1118">
                  {filteredPrograms.length}{' '}
                  {filteredPrograms.length === 1 ? 'Program' : 'Programs'}{' '}
                  Available
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1119">
                {filteredPrograms.map((program, index) => <Card key={index} className="h-full overflow-hidden hover:shadow-lg transition-shadow" data-id="element-1120">
                    <div className="h-48 overflow-hidden cursor-pointer" onClick={() => handleProgramClick(program)} data-id="element-1121">
                      <img src={program.image} alt={program.title} className="w-full h-full object-cover transition-transform hover:scale-105 duration-300" data-id="element-1122" />
                    </div>
                    <CardBody data-id="element-1123">
                      <div className="mb-2" data-id="element-1124">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#0099A1]/10 text-[#0099A1]" data-id="element-1125">
                          {program.category}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-[#374047] mb-2 cursor-pointer" onClick={() => handleProgramClick(program)} data-id="element-1126">
                        {program.title}
                      </h3>
                      <p className="text-[#374047]/80 mb-4 line-clamp-2" data-id="element-1127">
                        {program.description}
                      </p>
                      <div className="space-y-2 mb-4" data-id="element-1128">
                        <div className="flex items-center" data-id="element-1129">
                          <CalendarIcon className="h-4 w-4 text-[#0099A1] mr-2" data-id="element-1130" />
                          <span className="text-sm text-[#374047]" data-id="element-1131">
                            {program.duration}
                          </span>
                        </div>
                        <div className="flex items-center" data-id="element-1132">
                          <DollarSignIcon className="h-4 w-4 text-[#0099A1] mr-2" data-id="element-1133" />
                          <span className="text-sm text-[#374047]" data-id="element-1134">
                            {program.price}
                          </span>
                        </div>
                        <div className="flex items-center" data-id="element-1135">
                          <UsersIcon className="h-4 w-4 text-[#0099A1] mr-2" data-id="element-1136" />
                          <span className="text-sm text-[#374047]" data-id="element-1137">
                            {program.groupSize}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2" data-id="element-1138">
                        <Button variant="outline" className="flex-1" onClick={() => handleProgramClick(program)} data-id="element-1139">
                          View Details
                        </Button>
                        <Button variant="primary" className="flex-1" onClick={() => handleBookNow(program)} data-id="element-1140">
                          Book Now
                        </Button>
                      </div>
                    </CardBody>
                  </Card>)}
              </div>
            </> : <div className="text-center py-12" data-id="element-1141">
              <div className="mb-4" data-id="element-1142">
                <SearchIcon className="h-12 w-12 text-[#0099A1]/50 mx-auto" data-id="element-1143" />
              </div>
              <h3 className="text-xl font-bold text-[#374047] mb-2" data-id="element-1144">
                No programs found
              </h3>
              <p className="text-[#374047]/70 mb-6" data-id="element-1145">
                Try adjusting your filters or search term
              </p>
              <Button variant="outline" onClick={clearFilters} data-id="element-1146">
                Clear All Filters
              </Button>
            </div>}
        </div>
      </section>

      {/* Program Detail Modal */}
      {selectedProgram && <ProgramDetailModal program={selectedProgram} isOpen={isModalOpen} onClose={() => {
      setIsModalOpen(false);
      // Remove the program parameter from URL
      const url = new URL(window.location.href);
      url.searchParams.delete('program');
      window.history.pushState({}, '', url);
    }} data-id="element-1147" />}

      <CtaSection data-id="element-1148" />
    </div>;
}